import { useEffect } from "react";

type Options = {
  debounceMs?: number;
  refocus?: boolean;
  refocusOnlyIfVisible?: boolean;
};

const DEFAULTS: Required<Options> = {
  debounceMs: 150,
  refocus: true,
  refocusOnlyIfVisible: true,
};

function isIOSLikeDevice() {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  return (
    /iPhone|iPad|iPod/i.test(ua)
    || (/Mac/i.test(platform) && maxTouchPoints > 1)
  );
}

const TEXT_INPUT_TYPES = new Set([
  "text", "search", "email", "tel", "url", "password", "",
]);

function hasDropdownBehavior(el: HTMLElement): boolean {
  // ARIA attributes indicating dropdown/listbox
  if (el.getAttribute("role") === "combobox") return true;
  if (el.hasAttribute("aria-haspopup")) return true;
  if (el.hasAttribute("aria-controls")) return true;
  if (el.getAttribute("aria-autocomplete") === "list") return true;

  // Inside ReactSelect
  if (el.closest("[class*='react-select']")) return true;

  // Inside HeadlessUI components
  if (el.closest("[data-headlessui-state]")) return true;

  return false;
}

function isPlainTextInput(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el) return false;
  if (!(el instanceof HTMLElement)) return false;

  if (el instanceof HTMLTextAreaElement) {
    return !hasDropdownBehavior(el);
  }

  if (el instanceof HTMLInputElement) {
    const type = el.type.toLowerCase();
    if (!TEXT_INPUT_TYPES.has(type)) return false;
    return !hasDropdownBehavior(el);
  }

  return false;
}

function isFocusableNow(el: HTMLElement) {
  const anyEl = el as any;
  if (anyEl.disabled) return false;
  if (anyEl.readOnly) return false;
  return true;
}

function isInViewport(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  return (
    r.bottom > 0 &&
    r.right > 0 &&
    r.top < window.innerHeight &&
    r.left < window.innerWidth
  );
}

function safeFocus(el: HTMLElement) {
  try {
    (el as any).focus({ preventScroll: true });
  } catch {
    el.focus();
  }
}

export function AutofillScrollManager(opts: Options) {
  const o = { ...DEFAULTS, ...opts };

  useEffect(() => {
    if (isIOSLikeDevice()) {
      return;
    }

    let lastFocused: HTMLElement | null = null;
    let timer: number | null = null;

    let focusChangedByUser = false;
    const onFocusIn = () => {
      focusChangedByUser = true;
    };

    const onScroll = () => {
      const active = document.activeElement;

      if (
        isPlainTextInput(active)
        && isFocusableNow(active)
      ) {
        lastFocused = active;
        focusChangedByUser = false;
        active.blur();
      }

      if (!o.refocus) return;

      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;

        if (!lastFocused) return;
        if (!document.contains(lastFocused)) return;
        if (!isFocusableNow(lastFocused)) return;
        if (focusChangedByUser) return;
        if (o.refocusOnlyIfVisible && !isInViewport(lastFocused)) return;

        safeFocus(lastFocused);
      }, o.debounceMs);
    };

    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    document.addEventListener("focusin", onFocusIn, true);

    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true } as any);
      document.removeEventListener("focusin", onFocusIn, true);
      if (timer) window.clearTimeout(timer);
    };
  }, [o.debounceMs, o.refocus, o.refocusOnlyIfVisible]);

  return null;
}
